import { cn } from 'src/lib/utils';

type ISoftwareOnboardingHeader = {
  justify?: 'between' | 'end';
};

const SoftwareOnboardingHeader = ({
  children,
  justify = 'between',
}: React.PropsWithChildren<ISoftwareOnboardingHeader>) => {
  return (
    <header className="shrink-0 mb-[6vh] px-6 md:px-16">
      <div className={cn('max-w-260 w-full mx-auto flex justify-between gap-4', justify === 'end' && 'justify-end')}>
        {children}
      </div>
    </header>
  );
};

type ISoftwareOnboardingMain = {
  subtitle?: string;
  title: string;
  description?: string;
  noteText?: string | React.ReactNode;
  hintText?: string | React.ReactNode;
  className?: string;
  classes?: {
    header?: string;
    note?: string;
    wrapper?: string;
    subtitle?: string;
  };
};

const SoftwareOnboardingMain = ({
  title,
  subtitle,
  description,
  noteText,
  hintText,
  className,
  children,
  classes: { header, note, wrapper, subtitle: subtitleClass } = {},
}: React.PropsWithChildren<ISoftwareOnboardingMain>) => {
  return (
    <main className={cn('relative grow flex flex-col', className)}>
      <div className={cn('flex flex-col px-6 md:px-16', wrapper)}>
        <div className={cn('max-w-260 w-full mx-auto space-y-4 mb-5', header)}>
          {subtitle && (
            <h2
              className={cn(
                'text-xs font-semibold uppercase text-secondary-foreground tracking-[0.04em]',
                subtitleClass,
              )}
            >
              {subtitle}
            </h2>
          )}
          <h1 className="text-xl text-foreground">{title}</h1>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {noteText && (
            <p className={cn('italic text-xs text-muted-foreground whitespace-pre-wrap leading-[1.6]', note)}>
              {noteText}
            </p>
          )}
          {hintText && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{hintText}</p>}
        </div>
        {children}
      </div>
    </main>
  );
};

const SoftwareOnboardingLayout = ({ children }: React.PropsWithChildren) => {
  return <div className="flex flex-col h-dvh overflow-y-auto pt-10">{children}</div>;
};

type ISoftwareOnboardingFooter = {
  justify?: 'between' | 'end';
};

const SoftwareOnboardingFooter = ({
  children,
  justify = 'between',
}: React.PropsWithChildren<ISoftwareOnboardingFooter>) => {
  return (
    <footer className="sticky bottom-0 z-10 shrink-0 px-6 md:px-16 py-4 border-t bg-background mt-10">
      <div className={cn('max-w-260 w-full mx-auto flex justify-between gap-2', justify === 'end' && 'justify-end')}>
        {children}
      </div>
    </footer>
  );
};

export { SoftwareOnboardingLayout, SoftwareOnboardingHeader, SoftwareOnboardingFooter, SoftwareOnboardingMain };
